import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabase';

// Define the profile type
interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
}

// Define the profile context type
interface ProfileContextType {
  profile: Profile | null;
  loading: boolean;
  error: Error | null;
  updateProfile: (updates: Partial<Profile>) => Promise<void>;
  createProfile: (profile: Omit<Profile, 'created_at' | 'updated_at'>) => Promise<void>;
}

// Create the profile context
const ProfileContext = createContext<ProfileContextType>({
  profile: null,
  loading: false,
  error: null,
  updateProfile: async () => {},
  createProfile: async () => {},
});

// Custom hook to use the profile context
export const useProfile = () => useContext(ProfileContext);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Fetch profile when user changes
  useEffect(() => {
    const getProfile = async () => {
      if (!user) {
        setProfile(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch profile from Supabase
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) {
          // If profile doesn't exist, create one
          if (error.code === 'PGRST116') {
            // Profile not found
            if (user.email) {
              await createProfileHandler({
                id: user.id,
                email: user.email,
                display_name: user.email.split('@')[0],
                avatar_url: null,
              });
              
              // Fetch the newly created profile
              const { data: newProfile, error: fetchError } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', user.id)
                .single();
                
              if (fetchError) throw fetchError;
              setProfile(newProfile as Profile);
            }
          } else {
            throw error;
          }
        } else {
          setProfile(data as Profile);
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error);
          console.error('Error fetching profile:', error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    getProfile();
  }, [user]);

  // Update profile handler
  const updateProfileHandler = async (updates: Partial<Profile>) => {
    if (!user || !profile) return;

    try {
      setLoading(true);
      setError(null);

      // Make sure not to update id or email
      const { id, email, ...updatableFields } = updates;
      const safeUpdates = { ...updatableFields };

      // Update profile in Supabase
      const { data, error } = await supabase
        .from('profiles')
        .update(safeUpdates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data as Profile);
    } catch (error) {
      if (error instanceof Error) {
        setError(error);
        console.error('Error updating profile:', error.message);
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  // Create profile handler
  const createProfileHandler = async (newProfile: Omit<Profile, 'created_at' | 'updated_at'>) => {
    try {
      setLoading(true);
      setError(null);

      // Insert new profile in Supabase
      const { data, error } = await supabase
        .from('profiles')
        .insert([newProfile])
        .select()
        .single();

      if (error) throw error;

      setProfile(data as Profile);
    } catch (error) {
      if (error instanceof Error) {
        setError(error);
        console.error('Error creating profile:', error.message);
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    profile,
    loading,
    error,
    updateProfile: updateProfileHandler,
    createProfile: createProfileHandler,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}; 